import { TraceMap, eachMapping } from '@jridgewell/trace-mapping'

// --- Binary format (per-chunk) ---
// Header:  [magic: u32le "TFM3"] [nameCount: u32le]
//          [sourceCount: u32le] [entryCount: u32le]     = 16 bytes
// Names:   for each: [len: u16le] [utf8 bytes]
// Sources: for each: [len: u16le] [utf8 bytes]
// Entries: for each:
//   [line: u32le] [col: u32le] [nameIdx: u16le]
//   [sourceIdx: u16le] [sourceLine: u32le]        = 16 bytes × N
// Entries sorted by (line, col) for binary search.
// nameIdx=0xffff means no original function name.
// sourceIdx=0xffff means no original source location.

// --- Bundle format ---
// [chunkCount: u32le]
// for each chunk:
//   [nameLen: u16le][name: utf8][dataLen: u32le][funcmap: bytes]

const MAGIC = 0x334d4654 // "TFM3"
const HEADER_SIZE = 16
const ENTRY_SIZE = 16
const NO_NAME = 0xffff
const NO_SOURCE = 0xffff

export interface FuncMapSourceLocation {
  file: string
  line: number
  col?: number
}

export interface ResolvedFuncFrame {
  name?: string
  source?: FuncMapSourceLocation
}

export interface ResolvedFuncFrameSentinel {
  sentinel: true
}

export type ResolveFuncFrameResult =
  | ResolvedFuncFrame
  | ResolvedFuncFrameSentinel

// --- Generator (build time) ---

export interface GenerateFuncMapOpts {
  /** skip mappings where filter returns false for `m.source` */
  sourceFilter?: (source: string) => boolean
}

export function generateFuncMap(
  sourceMapJson: string,
  opts?: GenerateFuncMapOpts,
): Uint8Array {
  const map = new TraceMap(sourceMapJson)
  const sourceFilter = opts?.sourceFilter

  // collect unique names + entries
  const nameIndex = new Map<string, number>()
  const names: string[] = []
  const entries: {
    line: number
    col: number
    nameIdx: number
    source?: FuncMapSourceLocation
  }[] = []

  eachMapping(map, (m) => {
    // Filtered/nameless regions are kept as SENTINEL entries instead of being
    // dropped: the largest-<= lookup would otherwise resolve dependency-region
    // positions to the preceding app mapping - wrong culprit, and a name that
    // changes with every build's chunk layout, re-splitting error groups on
    // each deploy. A sentinel hit doubles as the runtime "foreign code"
    // signal for frame classification.
    if (sourceFilter && m.source && !sourceFilter(m.source)) {
      entries.push(_sentinelEntry(m.generatedLine, m.generatedColumn))
      return
    }

    const source =
      m.source && m.originalLine !== null
        ? {
            file: m.source,
            line: m.originalLine,
            col: m.originalColumn ?? 0,
          }
        : undefined
    if (!m.name && !source) {
      entries.push(_sentinelEntry(m.generatedLine, m.generatedColumn))
      return
    }

    let nameIdx = NO_NAME
    if (m.name) {
      const existingIdx = nameIndex.get(m.name)
      if (existingIdx === undefined) {
        nameIdx = names.length
        nameIndex.set(m.name, nameIdx)
        names.push(m.name)
      } else {
        nameIdx = existingIdx
      }
    }

    entries.push({
      line: m.generatedLine,
      col: m.generatedColumn,
      nameIdx,
      source,
    })
  })

  // sort by (line, col)
  entries.sort((a, b) => a.line - b.line || a.col - b.col)
  const collapsedEntries = collapseEntries(entries)

  // compute total size
  const encoder = new TextEncoder()
  const encodedNames = names.map((n) => encoder.encode(n))
  let namesSize = 0
  for (const enc of encodedNames) namesSize += 2 + enc.length
  const sourceIndex = new Map<string, number>()
  const sources: string[] = []
  for (const entry of collapsedEntries) {
    const source = entry.source
    if (!source || sourceIndex.has(source.file)) continue
    sourceIndex.set(source.file, sources.length)
    sources.push(source.file)
  }
  const encodedSources = sources.map((source) => encoder.encode(source))
  let sourcesSize = 0
  for (const enc of encodedSources) sourcesSize += 2 + enc.length
  const totalSize =
    HEADER_SIZE + namesSize + sourcesSize + collapsedEntries.length * ENTRY_SIZE

  // write
  const buf = new Uint8Array(totalSize)
  const view = new DataView(buf.buffer)

  // header
  view.setUint32(0, MAGIC, true)
  view.setUint32(4, names.length, true)
  view.setUint32(8, sources.length, true)
  view.setUint32(12, collapsedEntries.length, true)

  // names
  let offset = HEADER_SIZE
  for (const enc of encodedNames) {
    view.setUint16(offset, enc.length, true)
    offset += 2
    buf.set(enc, offset)
    offset += enc.length
  }

  // sources
  for (const enc of encodedSources) {
    view.setUint16(offset, enc.length, true)
    offset += 2
    buf.set(enc, offset)
    offset += enc.length
  }

  // entries
  for (const e of collapsedEntries) {
    view.setUint32(offset, e.line, true)
    view.setUint32(offset + 4, e.col, true)
    view.setUint16(offset + 8, e.nameIdx, true)
    view.setUint16(
      offset + 10,
      e.source ? (sourceIndex.get(e.source.file) ?? NO_SOURCE) : NO_SOURCE,
      true,
    )
    view.setUint32(offset + 12, e.source?.line ?? 0, true)
    offset += ENTRY_SIZE
  }

  return buf
}

function _sentinelEntry(
  line: number,
  col: number,
): {
  line: number
  col: number
  nameIdx: number
  source?: FuncMapSourceLocation
} {
  return { line, col, nameIdx: NO_NAME }
}

// Source maps emit a mapping per token; resolution only needs entries where
// the resolved (name, source file, source line) changes. Collapsing runs
// measured ~3x smaller than the uncollapsed format on real meter-values
// builds (index.ingest-mqtt: 68KB -> 25KB, node-cjs-shims: 856KB -> 272KB).
function collapseEntries(
  entries: {
    line: number
    col: number
    nameIdx: number
    source?: FuncMapSourceLocation
  }[],
): typeof entries {
  const collapsed: typeof entries = []
  for (const entry of entries) {
    const previous = collapsed.at(-1)
    if (previous && hasSameResolution(previous, entry)) continue
    collapsed.push(entry)
  }
  return collapsed
}

function hasSameResolution(
  left: {
    nameIdx: number
    source?: FuncMapSourceLocation
  },
  right: {
    nameIdx: number
    source?: FuncMapSourceLocation
  },
): boolean {
  if (left.nameIdx !== right.nameIdx) return false
  if (!left.source || !right.source) return !left.source && !right.source
  return (
    left.source.file === right.source.file &&
    left.source.line === right.source.line
  )
}

// --- Loaded representation ---

export interface LoadedFuncMap {
  names: string[]
  sources: string[]
  /** DataView over entries section */
  entries: DataView
  entryCount: number
}

export function loadFuncMap(buffer: ArrayBufferLike): LoadedFuncMap {
  const view = new DataView(buffer)
  if (view.getUint32(0, true) !== MAGIC) {
    throw new Error('Invalid func-map chunk')
  }
  const nameCount = view.getUint32(4, true)
  const sourceCount = view.getUint32(8, true)
  const entryCount = view.getUint32(12, true)

  const decoder = new TextDecoder()
  const names: string[] = []
  let offset = HEADER_SIZE
  for (let i = 0; i < nameCount; i++) {
    const len = view.getUint16(offset, true)
    offset += 2
    names.push(decoder.decode(new Uint8Array(buffer, offset, len)))
    offset += len
  }

  const sources: string[] = []
  for (let i = 0; i < sourceCount; i++) {
    const len = view.getUint16(offset, true)
    offset += 2
    sources.push(decoder.decode(new Uint8Array(buffer, offset, len)))
    offset += len
  }

  const entries = new DataView(buffer, offset, entryCount * ENTRY_SIZE)

  return { names, sources, entries, entryCount }
}

// --- Resolver (binary search) ---

export function resolveFuncFrame(
  map: LoadedFuncMap,
  line: number,
  col: number,
): ResolveFuncFrameResult | null {
  const entryIdx = _findEntryIndex(map, line, col)
  if (entryIdx === -1) return null

  const nameIdx = map.entries.getUint16(entryIdx * ENTRY_SIZE + 8, true)
  const name = nameIdx === NO_NAME ? undefined : map.names[nameIdx]
  const sourceIdx = map.entries.getUint16(entryIdx * ENTRY_SIZE + 10, true)
  const sourceFile =
    sourceIdx === NO_SOURCE ? undefined : map.sources[sourceIdx]
  const source = sourceFile
    ? {
        file: sourceFile,
        line: map.entries.getUint32(entryIdx * ENTRY_SIZE + 12, true),
      }
    : undefined
  if (!name && !source) return { sentinel: true }
  return { ...(name ? { name } : {}), ...(source ? { source } : {}) }
}

export function isFuncMapSentinelFrame(
  frame: ResolveFuncFrameResult,
): frame is ResolvedFuncFrameSentinel {
  return 'sentinel' in frame
}

function _findEntryIndex(
  map: LoadedFuncMap,
  line: number,
  col: number,
): number {
  const { entries, entryCount } = map
  if (entryCount === 0) return -1

  // binary search for largest entry <= (line, col)
  let lo = 0
  let hi = entryCount - 1
  let best = -1

  while (lo <= hi) {
    const mid = (lo + hi) >>> 1
    const off = mid * ENTRY_SIZE
    const eLine = entries.getUint32(off, true)
    const eCol = entries.getUint32(off + 4, true)

    if (eLine < line || (eLine === line && eCol <= col)) {
      best = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  return best
}

// --- Bundle encode/decode ---

export function encodeFuncMapBundle(
  chunks: Map<string, Uint8Array>,
): Uint8Array {
  const encoder = new TextEncoder()
  const encoded: { name: Uint8Array; data: Uint8Array }[] = []
  let totalSize = 4 // chunkCount header

  for (const [name, data] of chunks) {
    const nameBytes = encoder.encode(name)
    encoded.push({ name: nameBytes, data })
    totalSize += 2 + nameBytes.length + 4 + data.length
  }

  const buf = new Uint8Array(totalSize)
  const view = new DataView(buf.buffer)
  view.setUint32(0, chunks.size, true)

  let offset = 4
  for (const { name, data } of encoded) {
    view.setUint16(offset, name.length, true)
    offset += 2
    buf.set(name, offset)
    offset += name.length
    view.setUint32(offset, data.length, true)
    offset += 4
    buf.set(data, offset)
    offset += data.length
  }

  return buf
}

export function decodeFuncMapBundle(
  buffer: ArrayBufferLike,
): Map<string, LoadedFuncMap> {
  const view = new DataView(buffer)
  const decoder = new TextDecoder()
  const chunkCount = view.getUint32(0, true)
  const result = new Map<string, LoadedFuncMap>()

  let offset = 4
  for (let i = 0; i < chunkCount; i++) {
    const nameLen = view.getUint16(offset, true)
    offset += 2
    const name = decoder.decode(new Uint8Array(buffer, offset, nameLen))
    offset += nameLen
    const dataLen = view.getUint32(offset, true)
    offset += 4
    const chunkBuf = (buffer as ArrayBuffer).slice(offset, offset + dataLen)
    offset += dataLen
    result.set(name, loadFuncMap(chunkBuf))
  }

  return result
}
